import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOperatorAuth } from '../context/OperatorAuthContext';
import { ApiError } from '../api/client';
import { useFragmentToken } from '../hooks/useFragmentToken';
import ResendVerification from '../components/ResendVerification';

type State =
  | { kind: 'missing' }
  | { kind: 'verifying' }
  | { kind: 'verified' }
  | { kind: 'failed'; message: string };

/**
 * Landing page for the verification mail: /verify-email#token=…
 * Public: the link must work in a browser with no console session.
 */
const VerifyEmailPage: React.FC = () => {
  const { api, isAuthenticated } = useOperatorAuth();
  const token = useFragmentToken();
  const [state, setState] = useState<State>(token ? { kind: 'verifying' } : { kind: 'missing' });
  // The token is single-use. React StrictMode runs effects twice in dev; a
  // second POST would spend nothing and come back INVALID_TOKEN over the
  // first one's success. One request per page load, and no cancel flag, so
  // the one request that does go out is the one whose answer is shown.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    api
      .verifyEmail(token)
      .then(() => setState({ kind: 'verified' }))
      .catch((err: unknown) => {
        setState({
          kind: 'failed',
          message:
            err instanceof ApiError && err.code === 'INVALID_TOKEN'
              ? err.message
              : err instanceof ApiError && err.status === 400
                ? 'This link is not a valid verification link. Ask for a new one.'
                : err instanceof ApiError
                  ? err.message
                  : 'Could not verify the address. Try the link again.',
        });
      });
  }, [api, token]);

  const askAgain = isAuthenticated ? (
    <ResendVerification label="Send a new link" />
  ) : (
    <p className="auth-foot">
      <Link to="/login">Log in</Link> to send a new verification link.
    </p>
  );

  return (
    <main className="auth-page">
      <div className="auth-card">
        <h1>Verify your email</h1>
        {state.kind === 'verifying' && <p className="auth-sub" role="status">Verifying…</p>}
        {state.kind === 'verified' && (
          <>
            <p className="auth-notice" role="status">
              Email verified. This address can now be used to recover the account.
            </p>
            <p className="auth-foot">
              {isAuthenticated ? <Link to="/console">Go to the console</Link> : <Link to="/login">Log in</Link>}
            </p>
          </>
        )}
        {state.kind === 'failed' && (
          <>
            <div className="auth-error" role="alert">{state.message}</div>
            {askAgain}
          </>
        )}
        {state.kind === 'missing' && (
          <>
            <div className="auth-error" role="alert">
              This page needs the link from the verification email. Open that link again, or ask for a new one.
            </div>
            {askAgain}
          </>
        )}
      </div>
    </main>
  );
};

export default VerifyEmailPage;
